<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <i :class="iconClass" :style="iconStyle" v-if="isMdiIcon"></i>
  <IconifyIcon v-else-if="isIconify" :icon="iconName" :class="iconClass" :style="iconStyle" />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Icon as IconifyIcon } from '@iconify/vue'
import '@mdi/font/css/materialdesignicons.css'

const props = withDefaults(
  defineProps<{
    icon: string
    color?: string
    size?: string | number
    class?: string
  }>(),
  {
    color: 'currentColor',
    size: 20,
    class: '',
  },
)

// Vérifier si c'est une icône MDI (format $iconName, mdi:icon-name, ou mdi-icon-name)
const isMdiIcon = computed(() => {
  return (
    props.icon.startsWith('$') || props.icon.startsWith('mdi:') || props.icon.startsWith('mdi-')
  )
})

// Vérifier si c'est une icône Iconify
const isIconify = computed(() => {
  return !isMdiIcon.value && props.icon.includes(':')
})

// Nom de l'icône MDI (enlever le $ ou mdi: et convertir en format mdi-)
const iconName = computed(() => {
  if (props.icon.startsWith('$')) {
    // Convertir $magnify en mdi-magnify
    const name = props.icon.slice(1)
    // Convertir camelCase en kebab-case
    return name
      .replace(/([A-Z])/g, '-$1')
      .toLowerCase()
      .replace(/^-/, '')
  }
  if (props.icon.startsWith('mdi:')) {
    // Convertir mdi:icon-name en icon-name
    return props.icon.slice(4)
  }
  if (props.icon.startsWith('mdi-')) {
    // Format déjà correct : mdi-icon-name
    return props.icon.slice(4) // Enlever le préfixe mdi- pour la classe CSS
  }
  return props.icon
})

// Classe CSS pour l'icône MDI
const iconClass = computed(() => {
  if (isMdiIcon.value) {
    // Le nom de l'icône est déjà sans le préfixe mdi- grâce à iconName
    const iconClass = `mdi mdi-${iconName.value}`
    return `${iconClass} ${props.class}`.trim()
  }
  return props.class.trim()
})

// Style pour la couleur et la taille
const iconStyle = computed(() => {
  const styles: Record<string, string> = {}

  if (props.color) {
    styles.color =
      props.color === 'main-blue'
        ? 'rgb(var(--v-theme-main-blue))'
        : props.color === 'main-red'
          ? 'rgb(var(--v-theme-main-red))'
          : props.color === 'main-grey'
            ? 'rgb(var(--v-theme-main-grey))'
            : props.color === 'light-blue'
              ? 'rgb(var(--v-theme-light-blue))'
              : props.color === 'dark-grey'
                ? 'rgb(var(--v-theme-dark-grey))'
                : props.color === 'main-black'
                  ? 'rgb(var(--v-theme-main-black))'
                  : props.color === 'main-green'
                    ? 'rgb(var(--v-theme-main-green))'
                    : props.color
  }

  if (props.size) {
    const sizeValue = typeof props.size === 'number' ? `${props.size}px` : props.size
    styles.fontSize = sizeValue
    styles.width = sizeValue
    styles.height = sizeValue
  }

  return styles
})
</script>

<style scoped>
.mdi {
  font-family: 'Material Design Icons';
  font-weight: normal;
  font-style: normal;
  display: inline-block;
  line-height: 1;
  text-transform: none;
  letter-spacing: normal;
  word-wrap: normal;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  -moz-osx-font-smoothing: grayscale;
  font-feature-settings: 'liga';
}
</style>
